/**
 * Décale doucement un calque selon sa position dans la fenêtre : l'arrière-plan d'une
 * étape « dérive » plus lentement que le défilement, avec une inertie légère plutôt qu'un
 * calage brut sur la molette — une boucle `requestAnimationFrame` tourne en continu et
 * rapproche la position affichée de sa cible par petits pas (lissage exponentiel), ce
 * qu'un simple écouteur `scroll` ne peut pas donner : la valeur y « saute » à chaque
 * évènement, ici elle glisse.
 *
 * Sous `prefers-reduced-motion`, aucune boucle n'est même démarrée — le calque reste
 * immobile.
 */

import { useEffect, useRef } from 'react';

/** Part de l'écart comblée à chaque image : plus petit, plus fluide et « lourd » ; plus
    grand, plus réactif. 0.08 donne un temps de réponse d'environ 200 ms à 60 fps. */
const SMOOTHING = 0.08;

export function useParallax<T extends HTMLElement>(intensity = 60): React.RefObject<T | null> {
  const ref = useRef<T>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let current = 0;
    let frame = 0;

    const target = (): number => {
      const rect = node.getBoundingClientRect();
      const viewportCenter = window.innerHeight / 2;
      const elementCenter = rect.top + rect.height / 2;
      const distance = (elementCenter - viewportCenter) / viewportCenter;
      return -distance * intensity;
    };

    const tick = (): void => {
      current += (target() - current) * SMOOTHING;
      node.style.setProperty('--parallax-y', `${current.toFixed(1)}px`);
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [intensity]);

  return ref;
}
